import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';

const steps = [
  {
    id: 1,
    title: 'Add Your First Disciple',
    videoPlaceholder: true,
    videoUrl: null, // Replace with actual video URL when available
    description:
      'The heart of Together On Mission is the people God has placed in your life. In this step, you\'ll learn how to add someone you\'re walking with — a friend, a neighbor, a coworker — and set up reminders so you never forget to reach out.',
    bullets: [
      'Tap "Add Disciple" from your dashboard',
      'Enter their name, phone number, and email',
      'Set a reminder frequency (we recommend weekly)',
      'TOM will nudge you when it\'s time to connect',
    ],
  },
  {
    id: 2,
    title: 'Send Your First Message',
    videoPlaceholder: true,
    videoUrl: null,
    description:
      'When a reminder fires, TOM helps you send a meaningful message — not just a "hey." You\'ll get a Bible verse, a reflection question, and the option to write your own encouragement. Send it via text or email with one tap.',
    bullets: [
      'Open a disciple\'s profile from your dashboard',
      'Tap "Send Message" to compose',
      'Choose a verse from our curated library or pick your own',
      'Add a personal note and send via SMS or email',
      'Your message history is saved automatically',
    ],
  },
  {
    id: 3,
    title: 'Stay Consistent & Share Stories',
    videoPlaceholder: true,
    videoUrl: null,
    description:
      'Discipleship isn\'t a one-time event — it\'s a rhythm. TOM helps you build that rhythm with reminders, notes, and the ability to share stories of what God is doing. When you share, you encourage others to step out too.',
    bullets: [
      'Check your dashboard regularly — it sorts by urgency',
      'Use notes to track conversations and prayer requests',
      'Share a story from the Community tab to encourage others',
      'Adjust reminder frequency as the relationship grows',
      'Celebrate progress — every conversation matters',
    ],
  },
];

const HowToUseTOM = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [moduleComplete, setModuleComplete] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentUser?.uid) {
      loadProgress();
    }
  }, [currentUser]);

  const loadProgress = async () => {
    try {
      const progressRef = doc(db, 'users', currentUser.uid, 'trainingProgress', 'how-to-use-tom');
      const progressSnap = await getDoc(progressRef);
      if (progressSnap.exists()) {
        const data = progressSnap.data();
        setCompletedSteps(data.completedSteps || {});
        setModuleComplete(data.completed || false);
        // Resume from the first incomplete step
        const firstIncomplete = steps.findIndex(s => !data.completedSteps?.[s.id]);
        if (firstIncomplete >= 0 && !data.completed) {
          setCurrentStep(firstIncomplete);
        }
      }
    } catch (error) {
      console.error('Error loading training progress:', error);
    }
  };

  const saveProgress = async (newCompletedSteps, isModuleComplete) => {
    if (!currentUser?.uid) return;
    setSaving(true);
    try {
      const progressRef = doc(db, 'users', currentUser.uid, 'trainingProgress', 'how-to-use-tom');
      await setDoc(progressRef, {
        completedSteps: newCompletedSteps,
        completed: isModuleComplete,
        lastUpdated: serverTimestamp(),
        ...(isModuleComplete ? { completedAt: serverTimestamp() } : {}),
      }, { merge: true });
    } catch (error) {
      console.error('Error saving training progress:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    // Mark current step as complete
    const newCompleted = { ...completedSteps, [steps[currentStep].id]: true };
    setCompletedSteps(newCompleted);

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      saveProgress(newCompleted, false);
    } else {
      // Module complete
      setModuleComplete(true);
      saveProgress(newCompleted, true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGoToStep = (index) => {
    setCurrentStep(index);
  };

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;
  const totalCompleted = Object.values(completedSteps).filter(Boolean).length;

  if (moduleComplete) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto py-12 px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">You're Ready!</h1>
            <p className="text-gray-600 text-lg mb-2">
              You've completed the "How to Use TOM" training.
            </p>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              Now go add someone to your list and send your first message. 
              The Great Commission starts with one conversation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-[#0076BE] text-white rounded-xl font-semibold hover:bg-[#005f98] transition"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => navigate('/add-disciple')}
                className="px-6 py-3 bg-[#5ABA47] text-white rounded-xl font-semibold hover:bg-[#4a9939] transition"
              >
                Add Your First Disciple
              </button>
              <button
                onClick={() => navigate('/training-modules')}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition"
              >
                Back to Training
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/training-modules')}
            className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Training Modules
          </button>
          <span className="text-sm text-gray-400">{totalCompleted} of {steps.length} complete</span>
        </div>

        {/* Step Progress Bar */}
        <div className="flex items-center gap-2 mb-8">
          {steps.map((s, i) => (
            <button
              key={s.id}
              onClick={() => handleGoToStep(i)}
              className="flex-1 flex flex-col items-center gap-1 group"
            >
              <div className={`w-full h-2 rounded-full transition-colors ${
                completedSteps[s.id]
                  ? 'bg-[#5ABA47]'
                  : i === currentStep
                    ? 'bg-[#0076BE]'
                    : 'bg-gray-200'
              }`} />
              <span className={`text-xs font-medium transition-colors ${
                i === currentStep ? 'text-[#0076BE]' : completedSteps[s.id] ? 'text-[#5ABA47]' : 'text-gray-400'
              }`}>
                Step {s.id}
              </span>
            </button>
          ))}
        </div>

        {/* Step Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Video Area */}
          <div className="aspect-video bg-gray-900 relative flex items-center justify-center">
            {step.videoUrl ? (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={step.videoUrl}
                title={step.title}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-3">
                  <svg className="w-8 h-8 text-white/60" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <p className="text-white/40 text-sm">Video coming soon</p>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Step label */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-[#0076BE] bg-[#0076BE]/10 px-2.5 py-1 rounded-full">
                Step {step.id} of {steps.length}
              </span>
              {completedSteps[step.id] && (
                <span className="text-xs font-bold uppercase tracking-wider text-[#5ABA47] bg-green-50 px-2.5 py-1 rounded-full flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Complete
                </span>
              )}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h2>
            <p className="text-gray-600 leading-relaxed mb-6">{step.description}</p>

            {/* Bullet Points */}
            <ul className="space-y-3 mb-8">
              {step.bullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#5ABA47]/10 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-[#5ABA47]">{i + 1}</span>
                  </div>
                  <span className="text-gray-700">{bullet}</span>
                </li>
              ))}
            </ul>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <button
                onClick={handleBack}
                disabled={isFirstStep}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition ${
                  isFirstStep
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              <button
                onClick={handleNext}
                disabled={saving}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-white transition ${
                  isLastStep
                    ? 'bg-[#5ABA47] hover:bg-[#4a9939]'
                    : 'bg-[#0076BE] hover:bg-[#005f98]'
                } disabled:opacity-60`}
              >
                {saving ? (
                  'Saving...'
                ) : isLastStep ? (
                  <>
                    Complete Training
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                ) : (
                  <>
                    Next
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToUseTOM;
